import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { loadLocalFile, pruneLocalFiles } from '../lib/localMedia';
import { DEFAULT_GRID, type GridBox } from '../lib/gridLayout';
import { DEFAULT_WIDGET_STYLE, type WidgetStyle } from '../lib/panelStyle';
import type { Preset } from './presets';
import { activeProfile, storageKey } from './profile';

export type LayoutId = 'cinematic' | 'command' | 'bento' | 'split' | 'portrait-stack' | 'portrait-glass' | 'custom';
export type ThemeId = 'dark' | 'light' | 'oled' | 'amber' | 'contrast';
export type TransitionId = 'fade' | 'slide' | 'dissolve' | 'kenburns';

export interface MediaItem {
  id: string;
  url: string;
  kind: 'image' | 'video';
  title?: string;
  /** A file from this device: stored in IndexedDB, shown via an object URL, never synced. */
  local?: boolean;
  /** Displayed size, when known in advance (cloud photos). Used for orientation matching. */
  width?: number | null;
  height?: number | null;
  /** Always fill the screen, whatever the Photo fit setting (the built-in demo video). */
  alwaysFill?: boolean;
  /** When it was taken, as camera wall-clock time ("YYYY-MM-DDTHH:mm:ss", any zone suffix ignored). Cloud photos only; device files are read from EXIF when shown. */
  takenAt?: string | null;
  /** Where it was taken (photo GPS), when known. */
  latitude?: number | null;
  longitude?: number | null;
}

export interface Location {
  name: string;
  latitude: number;
  longitude: number;
  /** ISO 3166 alpha-2, used to pick the national weather-warning feed. */
  countryCode?: string;
  /** First-level region (e.g. "Comunidad de Madrid"), used to filter warnings. */
  region?: string;
}

export interface FeedSource {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
}

export interface CalendarSource extends FeedSource {
  color: string;
}

/** A cloud folder whose photos/videos join the slideshow (expanded at runtime). */
export interface CloudFolder {
  id: string;
  provider: 'onedrive';
  folderId: string;
  name: string;
  /** SharePoint library or shared drive; unset = the user's own OneDrive. */
  driveId?: string;
  /** Include photos from every sub-folder (default true when unset). */
  includeSubfolders?: boolean;
}

export interface Settings {
  layout: LayoutId;
  /** Layout used automatically when the screen is taller than it is wide ('same' = no switch). */
  layoutPortrait: LayoutId | 'same';
  theme: ThemeId;
  media: {
    items: MediaItem[];
    cloudFolders: CloudFolder[];
    intervalSec: number;
    transition: TransitionId;
    shuffle: boolean;
    overlayColor: string;
    overlayOpacity: number; // 0..1
    /** contain: whole photo with a blurred backdrop; cover: fill the screen, cropping edges. */
    fit: 'contain' | 'cover';
    /** Cloud photo resolution: the original file, or a rendition bounded to 4K / 1080p. */
    quality: 'original' | '4k' | '1080';
    /** Only show photos whose shape matches the layout (landscape vs portrait). */
    matchOrientation: boolean;
    /** Play the built-in demo video when no photos of your own are chosen. */
    demo: boolean;
  };
  /** Size of clock, widgets and news. auto = proportional to screen width (1920px = 1×). */
  display: { scaleMode: 'auto' | 'fixed'; scale: number };
  clock: { enabled: boolean; hour12: boolean; showSeconds: boolean; timeZone: string; showDate: boolean };
  weather: {
    enabled: boolean;
    location: Location | null;
    unit: 'celsius' | 'fahrenheit';
    windUnit: 'kmh' | 'mph' | 'ms';
    showHourly: boolean;
    showDaily: boolean;
    showFeelsLike: boolean;
    showRain: boolean;
    showWind: boolean;
    showHumidity: boolean;
    showUv: boolean;
    showSun: boolean;
    showWarnings: boolean;
  };
  calendar: {
    enabled: boolean;
    sources: CalendarSource[];
    days: number;
    outlook: boolean;
    /** Outlook calendar ids the user switched off (all others are shown). */
    outlookHidden: string[];
    /** list = agenda for the next N days; day / week / month = calendar grids. */
    view: 'list' | 'day' | 'week' | 'month';
    /** 0 = Sunday, 1 = Monday. */
    weekStartsOn: 0 | 1;
    /** Text size multiplier for the calendar (0.85 … 1.4). */
    textSize: number;
    /** How each event is marked: a coloured dot, a bar down its left edge, or a tinted block. */
    eventStyle: 'dot' | 'bar' | 'block';
    showHeader: boolean;
    showLocation: boolean;
    showEndTime: boolean;
    /** Highlight colour for today ('' = the theme accent). */
    todayColor: string;
  };
  news: {
    enabled: boolean;
    sources: FeedSource[];
    speed: number;
    /** ticker: scrolling headlines; card: a rotating story with picture; both. */
    display: 'ticker' | 'card' | 'both';
    cardIntervalSec: number;
  };
  lists: {
    enabled: boolean;
    /** local: lists stored with these settings; microsoft: Microsoft To Do lists. */
    source: 'local' | 'microsoft';
    /** Microsoft To Do list ids switched off (all others are shown). */
    microsoftHidden: string[];
  };
  /** Where and when the photo on screen was taken, with a map. Hidden for photos without that information. */
  photoInfo: {
    enabled: boolean;
    showCity: boolean;
    showDistrict: boolean;
    showCountry: boolean;
    showDate: boolean;
    showTime: boolean;
    showMap: boolean;
    /** fixed: the map stays at `zoom`; gradual: it zooms from `zoomFrom` to `zoom` while the photo is on screen. */
    zoomMode: 'fixed' | 'gradual';
    zoom: number;
    zoomFrom: number;
    /** Map look, by id from the server's list (standard, topo, humanitarian…). */
    mapStyle: string;
    /** Darken the map (inverted colours) on the dark themes. */
    mapDark: boolean;
  };
  /** Widget panels: background, shadow, corners (Settings → Appearance → Widget style). */
  widgetStyle: WidgetStyle;
  /** Font ids from lib/fonts.ts; heading 'same' = the body font. */
  fonts: { body: string; heading: string };
  /** The Custom layout: widget positions on the grid, one arrangement per screen shape (see lib/gridLayout.ts). */
  customLayout: { landscape: GridBox[]; portrait: GridBox[] };
  customCss: string;
  /** Photos-only mode: hide every widget (clock, weather, calendar, lists, news) without changing their own settings. */
  photosOnly: boolean;
  /** Saved layouts (see store/presets.ts). */
  presets: Preset[];
}

export const LAYOUTS: Array<{ id: LayoutId; name: string; description: string }> = [
  { id: 'cinematic', name: 'Cinematic Fullscreen', description: 'Edge-to-edge media with a minimal clock and ticker.' },
  { id: 'command', name: 'Command Center', description: 'Media with a stacked column of widgets on the right.' },
  { id: 'bento', name: 'Modular Bento Grid', description: 'Glass tiles arranged in a grid over your media.' },
  { id: 'split', name: 'Split Sidebar', description: 'A solid sidebar of widgets beside the media.' },
  { id: 'portrait-stack', name: 'Portrait Stack', description: 'Tall screens: photo on top, widgets below.' },
  { id: 'portrait-glass', name: 'Portrait Glass', description: 'Tall screens: full-height photo, glass widgets at the bottom.' },
  { id: 'custom', name: 'Custom', description: 'Your own arrangement: move and resize the widgets on screen.' },
];

export const THEMES: Array<{ id: ThemeId; name: string }> = [
  { id: 'dark', name: 'Modern Dark' },
  { id: 'light', name: 'Light' },
  { id: 'oled', name: 'OLED Pure Black' },
  { id: 'amber', name: 'Warm Amber' },
  { id: 'contrast', name: 'High Contrast' },
];

export const TRANSITIONS: Array<{ id: TransitionId; name: string }> = [
  { id: 'fade', name: 'Fade' },
  { id: 'dissolve', name: 'Cross-dissolve' },
  { id: 'slide', name: 'Slide' },
  { id: 'kenburns', name: 'Zoom (Ken Burns)' },
];

export const INTERVAL_MIN = 5;
export const INTERVAL_MAX = 3600;

export const defaultSettings: Settings = {
  layout: 'cinematic',
  layoutPortrait: 'portrait-stack',
  theme: 'dark',
  media: {
    items: [],
    cloudFolders: [],
    intervalSec: 30,
    transition: 'kenburns',
    shuffle: true,
    overlayColor: '#000000',
    overlayOpacity: 0.25,
    fit: 'contain',
    quality: 'original',
    matchOrientation: false,
    demo: true,
  },
  display: { scaleMode: 'auto', scale: 1 },
  clock: { enabled: true, hour12: false, showSeconds: false, timeZone: '', showDate: true },
  weather: {
    enabled: true,
    location: null,
    unit: 'celsius',
    windUnit: 'kmh',
    showHourly: true,
    showDaily: true,
    showFeelsLike: true,
    showRain: true,
    showWind: false,
    showHumidity: false,
    showUv: false,
    showSun: false,
    showWarnings: true,
  },
  calendar: {
    enabled: true,
    sources: [],
    days: 7,
    outlook: true,
    outlookHidden: [],
    view: 'list',
    weekStartsOn: 1,
    textSize: 1,
    eventStyle: 'dot',
    showHeader: true,
    showLocation: true,
    showEndTime: false,
    todayColor: '',
  },
  news: {
    enabled: true,
    sources: [
      { id: 'goodnews', name: 'Good News Network', url: 'https://www.goodnewsnetwork.org/feed/', enabled: true },
      { id: 'positive', name: 'Positive News', url: 'https://www.positive.news/feed/', enabled: true },
      { id: 'optimist', name: 'The Optimist Daily', url: 'https://www.optimistdaily.com/feed/', enabled: true },
      { id: 'cheerful', name: 'Reasons to be Cheerful', url: 'https://reasonstobecheerful.world/feed/', enabled: true },
      { id: 'allsides', name: 'AllSides', url: 'https://openrss.org/feed/www.allsides.com/unbiased-balanced-news', enabled: false },
      { id: 'bbc', name: 'BBC News', url: 'https://feeds.bbci.co.uk/news/rss.xml', enabled: false },
    ],
    speed: 60,
    display: 'ticker',
    cardIntervalSec: 20,
  },
  lists: { enabled: true, source: 'local', microsoftHidden: [] },
  photoInfo: {
    enabled: true,
    showCity: true,
    showDistrict: true,
    showCountry: true,
    showDate: true,
    showTime: true,
    showMap: true,
    zoomMode: 'fixed',
    zoom: 12,
    zoomFrom: 4,
    mapStyle: 'standard',
    mapDark: true,
  },
  widgetStyle: DEFAULT_WIDGET_STYLE,
  fonts: { body: 'system', heading: 'same' },
  customLayout: { landscape: DEFAULT_GRID.landscape, portrait: DEFAULT_GRID.portrait },
  customCss: '',
  photosOnly: false,
  presets: [],
};

export const SCALE_STEPS = [
  { value: 0.8, name: 'S' },
  { value: 1, name: 'M' },
  { value: 1.25, name: 'L' },
  { value: 1.5, name: 'XL' },
  { value: 2, name: 'XXL' },
];

export const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

interface SettingsStore extends Settings {
  update: <K extends keyof Settings>(key: K, patch: Partial<Settings[K]> | Settings[K]) => void;
  replaceAll: (s: Partial<Settings>) => void;
  reset: () => void;
}

function merge<T>(current: T, patch: Partial<T> | T): T {
  if (typeof current === 'object' && current !== null && !Array.isArray(current) && typeof patch === 'object') {
    return { ...current, ...patch };
  }
  return patch as T;
}

/** Shape safe to send to the server: device-only files are never uploaded. */
export function syncableSettings(s: Settings): Settings {
  return { ...s, media: { ...s.media, items: s.media.items.filter((i) => !i.local) } };
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Fills in defaults one level deep, so settings saved by an older version (or another
 * screen on an older version) gain new fields instead of leaving them undefined.
 */
export function withDefaults(incoming: Partial<Settings> | null | undefined): Settings {
  const out: Record<string, unknown> = { ...defaultSettings };
  for (const [k, v] of Object.entries(incoming ?? {})) {
    if (!(k in defaultSettings) || v === undefined) continue;
    const d = (defaultSettings as unknown as Record<string, unknown>)[k];
    out[k] = isPlainObject(d) && isPlainObject(v) ? { ...d, ...v } : v;
  }
  const s = out as unknown as Settings;
  // Screens still on the original BBC/Guardian defaults get the new default feeds;
  // anyone who added or removed feeds keeps their own list.
  const ids = s.news.sources.map((f) => f.id).sort().join(',');
  if (ids === 'bbc,guardian' || ids === 'bbc') s.news = { ...s.news, sources: defaultSettings.news.sources };
  return s;
}

export function pickSettings(s: Settings): Settings {
  const { layout, layoutPortrait, theme, display, media, clock, weather, calendar, news, lists, photoInfo } = s;
  const { widgetStyle, fonts, customLayout, customCss, photosOnly, presets } = s;
  return {
    layout,
    layoutPortrait,
    theme,
    display,
    media,
    clock,
    weather,
    calendar,
    news,
    lists,
    photoInfo,
    widgetStyle,
    fonts,
    customLayout,
    customCss,
    photosOnly,
    presets,
  };
}

export const useSettings = create<SettingsStore>()(
  persist(
    (set) => ({
      ...defaultSettings,
      update: (key, patch) => set((s) => ({ [key]: merge(s[key], patch) }) as Partial<SettingsStore>),
      replaceAll: (incoming) =>
        set((s) => {
          const next = withDefaults(incoming);
          // Keep this device's local files when applying settings from another screen.
          const local = s.media.items.filter((i) => i.local);
          return { ...next, media: { ...next.media, items: [...next.media.items.filter((i) => !i.local), ...local] } };
        }),
      reset: () => {
        void pruneLocalFiles(new Set()).catch(() => undefined);
        set({ ...defaultSettings });
      },
    }),
    {
      // Per-account key (see store/profile.ts); switchProfile() re-points it on sign-in/out.
      name: storageKey('kcl-canvas-settings', activeProfile()),
      version: 1,
      merge: (persisted, current) => ({ ...current, ...withDefaults(persisted as Partial<Settings>) }),
      // Local files are kept in IndexedDB; their object URLs die with the page, so persist
      // the item without its URL and rebuild it in restoreLocalMedia().
      partialize: (s) => {
        const picked = pickSettings(s);
        return { ...picked, media: { ...picked.media, items: picked.media.items.map((i) => (i.local ? { ...i, url: '' } : i)) } };
      },
    },
  ),
);

let restoring: Promise<void> | null = null;

/** Recreates object URLs for device files after a reload; drops items whose file is gone. */
export function restoreLocalMedia(force = false): Promise<void> {
  if (force) restoring = null;
  return (restoring ??= doRestoreLocalMedia());
}

async function doRestoreLocalMedia(): Promise<void> {
  const items = useSettings.getState().media.items;
  const local = items.filter((i) => i.local && !i.url);
  if (!local.length) {
    void pruneLocalFiles(new Set(items.filter((i) => i.local).map((i) => i.id))).catch(() => undefined);
    return;
  }
  const restored = new Map<string, string>();
  await Promise.all(
    local.map(async (i) => {
      const blob = await loadLocalFile(i.id).catch(() => undefined);
      if (blob) restored.set(i.id, URL.createObjectURL(blob));
    }),
  );
  const current = useSettings.getState().media.items;
  useSettings.getState().update('media', {
    items: current
      .map((i) => (i.local && !i.url ? (restored.has(i.id) ? { ...i, url: restored.get(i.id)! } : null) : i))
      .filter((i): i is MediaItem => i !== null),
  });
}
